/* eslint-disable complexity */
import { find_submitted_verification_answer } from './manage_verification_submitted_value_helpers';

const VERIFICATION_FIELD_GROUPS = [
  { match_terms: ['kyc'], fields: [{ key: 'id_number', label: 'ID Number' }, { key: 'surname', label: 'Surname' }] }, { match_terms: ['aml'], fields: [{ key: 'id_number', label: 'ID Number' }, { key: 'first_name', label: 'First name' }, { key: 'surname', label: 'Surname' }] },
  { match_terms: ['cipc'], fields: [{ key: 'year', label: 'Year' }, { key: 'unique_company_identifier', label: 'Company identifier' }, { key: 'company_type_code', label: 'Company type code' }] },
  { match_terms: ['credit'], fields: [{ key: 'consumer_id_number', label: 'Consumer ID number' }] }, { match_terms: ['id verification', 'identity', 'idv'], fields: [{ key: 'id_number', label: 'ID Number' }] }
];

const PREFERRED_RESULT_KEYS = [
  'id_number',
  'identity_number',
  'surname',
  'last_name',
  'first_name',
  'first_names',
  'date_of_birth',
  'dob',
  'company_name',
  'registration_number'
];

function read_result_data(result_data, key) {
  return Reflect.get(result_data || {}, key);
}

function value_is_present(value) {
  return value !== undefined && value !== null && value !== '';
}
function compact_rows(rows) {
  return rows.filter(function has_value(row) {
    return row.value !== '-';
  });
}

function build_check_run_detail_rows(context) {
  const response_time = context.raw_status.response_time
    || context.raw_status.duration
    || context.raw_status.results_data?.response_time;

  return compact_rows([
    { label: 'Run At', value: context.format_detail_value(context.raw_status.timestamp || context.raw_log.created_at || context.item.last_run) },
    { label: 'Run By', value: context.format_detail_value(context.raw_status.run_by || context.raw_log.created_by || context.raw_log.user || 'System (Auto)') },
    { label: 'Status', value: context.format_detail_value(context.item.status) },
    { label: 'Service', value: context.format_detail_value(context.raw_status.service_name || context.item.check) },
    { label: 'Response Time', value: context.format_detail_value(response_time) }
  ]);
}

function build_result_data_row(context, key) {
  return {
    label: context.format_field_label(key),
    value: context.format_detail_value(read_result_data(context.result_data, key))
  };
}

function build_rows_from_result_data(context) {
  return PREFERRED_RESULT_KEYS
    .filter(function has_result_value(key) {
      return value_is_present(read_result_data(context.result_data, key));
    })
    .map(function map_result_key(key) {
      return build_result_data_row(context, key);
    });
}

function get_match_label(total_count, matched_count, context) {
  if (!total_count) {
    return context.format_detail_value(context.raw_status.message || context.item.impact);
  }
  return matched_count === total_count ? 'Match' : 'No Match';
}

function get_confidence(total_count, matched_count, context) {
  if (!total_count) {
    return context.format_detail_value(context.result_data.confidence_score || context.result_data.score);
  }
  return `${Math.round((matched_count / total_count) * 100)}%`;
}

function build_check_result_rows(context) {
  const matched_count = context.field_results.filter(function field_matches(field) {
    return field.match;
  }).length;
  const total_count = context.field_results.length;

  return compact_rows([
    { label: 'Match Status', value: get_match_label(total_count, matched_count, context), tone: context.item.status_tone },
    { label: 'Confidence Score', value: get_confidence(total_count, matched_count, context), tone: context.item.status_tone },
    { label: 'Source', value: context.format_detail_value(context.result_data.source || context.result_data.provider || context.item.source) },
    { label: 'Response Code', value: context.format_detail_value(context.result_data.response_code || context.raw_status.response_code) }
  ]);
}

function build_current_check_log_entry(item = {}) {
  if (!item.last_run || item.last_run === '-') return [];

  return [{
    id: `${item.id}-current`,
    status: item.status,
    status_tone: item.status_tone,
    detail: item.impact || 'Verification result recorded',
    last_run: item.last_run
  }];
}

function build_expected_result_detail_rows(context) {
  const match_state = resolve_default_match_state(context.item);
  return context.expected_fields.map(function build_expected_row(field) {
    const submitted_answer = find_submitted_verification_answer(context, field);
    return {
      key: `${context.item.id}-${field.key}`,
      label: field.label,
      match_state,
      user_answer: context.format_detail_value(submitted_answer),
      api_value: context.format_detail_value(context.item.status),
      status_label: get_match_state_label(match_state),
      status_tone: get_match_state_tone(match_state)
    };
  });
}

function build_result_summary_detail_row(context) {
  const match_state = resolve_default_match_state(context.item);
  return {
    key: `${context.item.id}-result-summary`,
    label: context.item.check || 'Verification result',
    match_state,
    user_answer: '-',
    api_value: context.format_detail_value(context.item.status),
    status_label: get_match_state_label(match_state),
    status_tone: get_match_state_tone(match_state)
  };
}

function build_field_result_row(context, entry) {
  const field_name = entry[0];
  const result = entry[1];
  const match_state = normalize_match_state(result);
  return {
    key: field_name,
    label: context.format_field_label(field_name),
    match: match_state,
    match_state,
    user_answer: context.format_detail_value(result?.user_answer),
    api_value: context.format_detail_value(result?.api_value),
    message: context.format_detail_value(
      result?.message
      || result?.detail
      || result?.reason
      || result?.status_message
    ),
    status_label: get_match_state_label(match_state),
    status_tone: get_match_state_tone(match_state)
  };
}

function normalize_match_state(result = {}) {
  if (result?.match === true) return true;
  if (result?.match === false) return false;

  const status_value = String(
    result?.match_status
    || result?.status
    || result?.outcome
    || ''
  ).trim().toLowerCase();

  if (['match', 'matched', 'yes', 'pass', 'verified', 'success'].includes(status_value)) return true;
  if (['mismatch', 'no', 'fail', 'failed', 'error'].includes(status_value)) return false;
  return null;
}

function resolve_default_match_state(item = {}) {
  if (item?.status_tone === 'success') return true;
  if (item?.status_tone === 'danger') return false;
  return null;
}

function get_match_state_label(match_state) {
  if (match_state === true) return 'Match';
  if (match_state === false) return 'Mismatch';
  return 'Pending';
}

function get_match_state_tone(match_state) {
  if (match_state === true) return 'success';
  if (match_state === false) return 'danger';
  return 'warning';
}

function group_matches_check_name(group, check_name) {
  return group.match_terms.some(function term_matches(term) {
    return check_name.includes(term);
  });
}

function find_verification_field_group(check_name) {
  return VERIFICATION_FIELD_GROUPS.find(function group_matches(group) {
    return group_matches_check_name(group, check_name);
  });
}

function pick_summary_rows(rows_from_fields, rows_from_data) {
  const rows = rows_from_fields.length ? rows_from_fields : rows_from_data;
  return rows.slice(0, 6);
}
function get_result_detail_rows(component, item) {
  const field_results = component.get_check_field_results(item);
  if (field_results.length) return field_results;
  if (!item?.raw_status && !item?.raw_log) return [];

  const result_summary = component.get_automatic_result_summary(item);
  const expected_fields = component.get_expected_verification_fields(item);
  const context = {
    item,
    result_summary,
    expected_fields,
    format_detail_value: component.format_detail_value,
    get_case_forms_for_check: component.get_case_forms_for_check
  };
  return expected_fields.length
    ? build_expected_result_detail_rows(context)
    : [build_result_summary_detail_row(context)];
}

export const manageVerificationResultMethods = {
  get_automatic_result_summary(item = {}) {
    const field_results = this.get_check_field_results(item);
    if (field_results.length) {
      const mismatches = field_results.filter(function mismatch(field) {
        return field.match === false;
      }).length;
      return mismatches
        ? `${mismatches} mismatched field${mismatches === 1 ? '' : 's'} found.`
        : `${field_results.length} field${field_results.length === 1 ? '' : 's'} matched.`;
    }

    const history_entry = this.get_check_history(item).at(0);
    return history_entry?.detail || item?.impact || item?.source || 'Verification result recorded.';
  },
  get_check_result_data(item = {}) {
    const results_data = item?.raw_status?.results_data;
    return results_data && typeof results_data === 'object' ? results_data : {};
  },
  get_check_run_details(item = {}) {
    return build_check_run_detail_rows({
      raw_status: item?.raw_status || {},
      raw_log: item?.raw_log || {},
      item,
      format_detail_value: this.format_detail_value
    });
  },
  get_check_summary_rows(item = {}) {
    const result_data = this.get_check_result_data(item);
    const field_results = this.get_check_field_results(item);
    const rows_from_fields = field_results.map(function map_field(field) {
      return { label: field.label, value: field.user_answer };
    });
    const rows_from_data = build_rows_from_result_data({
      result_data,
      format_field_label: this.format_field_label,
      format_detail_value: this.format_detail_value
    });

    return pick_summary_rows(rows_from_fields, rows_from_data);
  },
  get_check_result_rows(item = {}) {
    return build_check_result_rows({
      raw_status: item?.raw_status || {},
      result_data: this.get_check_result_data(item),
      field_results: this.get_check_field_results(item),
      item,
      format_detail_value: this.format_detail_value
    });
  },
  get_check_log_entries(item = {}) {
    const entries = this.get_check_history(item);
    return entries.length ? entries.slice(0, 5) : build_current_check_log_entry(item);
  },
  get_check_history_rows() {
    return this.selected_case_checks
      .filter(function not_manual(item) {
        return item?.verification_mode !== 'manual';
      })
      .map((item) => ({
        id: item.id,
        type: item.check,
        status: item.status,
        status_tone: item.status_tone,
        run_at: item.last_run,
        run_by: this.get_check_history_run_by(item),
        duration: this.get_check_history_duration(item),
        result: this.get_automatic_result_summary(item)
      }));
  },
  get_check_history_run_by(item = {}) {
    const raw_status = item?.raw_status || {};
    const raw_log = item?.raw_log || {};
    const run_by = raw_status.run_by || raw_log.created_by || raw_log.user || 'System (Auto)';

    return this.format_detail_value(run_by);
  },
  get_check_history_duration(item = {}) {
    const raw_status = item?.raw_status || {};
    return this.format_detail_value(raw_status.response_time || raw_status.duration || raw_status.results_data?.response_time);
  },
  get_check_field_results(item = {}) {
    const field_results = item?.raw_status?.field_results;
    if (!field_results || typeof field_results !== 'object') return [];

    const context = {
      format_field_label: this.format_field_label,
      format_detail_value: this.format_detail_value
    };
    return Object.entries(field_results).map(function map_field_result(entry) {
      return build_field_result_row(context, entry);
    });
  },
  get_check_result_detail_rows(item = {}) {
    return get_result_detail_rows(this, item);
  },
  get_expected_verification_fields(item = {}) {
    const check_name = String(`${item.check || ''} ${item.source || ''}`).toLowerCase();
    const field_group = find_verification_field_group(check_name);
    return field_group ? field_group.fields : [];
  },
  get_check_passed_chips(item = {}) {
    return this.get_check_result_detail_rows(item).slice(0, 4).map(function map_chip(row) {
      return {
        key: `${item.id}-result-${row.key}`,
        label: row.label,
        match: row.match_state === true,
        state: row.match_state === true ? 'success' : row.match_state === false ? 'danger' : 'pending'
      };
    });
  },
  get_check_history(item = {}) {
    if (Array.isArray(item?.history) && item.history.length) return item.history;
    if (!item?.raw_log) return [];

    return [{
      id: item.id,
      status: item.status,
      status_tone: item.status_tone,
      detail: item.impact,
      last_run: item.last_run,
      raw_log: item.raw_log
    }];
  }
};
